// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/campaigns.test.tsx
import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";
import { type AskPunttProjectListItem } from "@mg/schemas/src/christo/askPunttProject";
import {
  CircleNotch,
  Clock,
  FolderSimplePlus,
  MagnifyingGlass,
  Plus,
  User,
  Users,
} from "@phosphor-icons/react";
import {
  Button,
  Callout,
  Checkbox,
  Flex,
  Popover,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { campaignRoute, campaignSearch } from "./campaignRoute";
import { useStartNewProjectChat } from "./useStartNewProjectChat";
import { projectMemberLabel } from "./utils/projectListMembers";

import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import {
  interactiveSurfaceClasses,
  interactiveSurfaceIconClasses,
} from "../../components/InteractiveSurface";
import { PageHeader } from "../../components/PageHeader";
import { ToolbarViewToggle } from "../../components/toolbar/ToolbarViewToggle";
import {
  TOOLBAR_CONTROL_SIZE,
  TOOLBAR_ICON_SIZE,
} from "../../components/toolbarControls";
import {
  useAskPunttProjectMemberOptions,
  useAskPunttProjects,
} from "../../utils/queries/askPunttProjects";

const VIEW_STORAGE_KEY = "askpuntt-projects-view";

type ProjectsView = "grid" | "list";

function getStoredView(): ProjectsView {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "list" || stored === "grid") return stored;
  } catch {
    // ignore
  }
  return "grid";
}

export function formatProjectDate(iso: string | Date | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ProjectMembersFooter({
  members,
  projectId,
}: {
  members: AskPunttChatUserSummary[];
  projectId: string;
}) {
  const count = members.length;
  if (count === 0) {
    return (
      <span
        className="flex items-center gap-1 text-puntt-neutral-gray-9"
        data-testid={`campaign-row-${projectId}-members`}
        aria-label="0 participants"
      >
        <Users size={14} aria-hidden="true" />
        <Text size="1">0</Text>
      </span>
    );
  }

  const visible = members.slice(0, 3);
  return (
    <span
      className="flex items-center gap-1"
      data-testid={`campaign-row-${projectId}-members`}
      aria-label={`${count} participant${count === 1 ? "" : "s"}`}
    >
      <Flex gap="0" align="center" className="-space-x-1">
        {visible.map((m) => (
          <AvatarWithInitials
            key={m._id}
            size={6}
            name={projectMemberLabel(m)}
            avatar={m.avatar ?? undefined}
            colorKey={m._id}
            backgroundColor={m.avatarBackgroundColor ?? undefined}
            textColor={m.avatarTextColor ?? undefined}
            className="ring-2 ring-base-white"
            aria-hidden="true"
          />
        ))}
      </Flex>
      {count > 3 ? (
        <Text size="1" className="text-puntt-neutral-gray-9">
          +{count - 3}
        </Text>
      ) : null}
    </span>
  );
}

function ProjectCard({
  project,
  onOpen,
}: {
  project: AskPunttProjectListItem;
  onOpen: (projectId: string) => void;
}) {
  const members = project.members ?? [];
  const updatedLabel = formatProjectDate(project.updatedAt);

  return (
    <button
      type="button"
      onClick={() => onOpen(project._id)}
      data-testid={`campaign-row-${project._id}`}
      data-project-id={project._id}
      aria-label={`Open project ${project.name}`}
      className={`${interactiveSurfaceClasses({
        interactive: true,
      })} flex min-h-[140px] flex-col justify-between p-5 text-left`}
    >
      <div className="min-w-0">
        <Text
          as="div"
          size="4"
          weight="bold"
          className="truncate text-puntt-neutral-gray-12"
        >
          {project.name}
        </Text>
        {project.description ? (
          <Text
            size="2"
            className="mt-2 line-clamp-2 text-puntt-neutral-gray-10"
          >
            {project.description}
          </Text>
        ) : null}
      </div>
      <Flex justify="end" align="center" gap="3" className="mt-4">
        {updatedLabel ? (
          <span
            className="flex items-center gap-1 text-puntt-neutral-gray-9"
            data-testid={`campaign-row-${project._id}-updated`}
          >
            <Clock size={14} aria-hidden="true" />
            <Text size="1">{updatedLabel}</Text>
          </span>
        ) : null}
        <ProjectMembersFooter members={members} projectId={project._id} />
      </Flex>
    </button>
  );
}

function ProjectListRow({
  project,
  onOpen,
}: {
  project: AskPunttProjectListItem;
  onOpen: (projectId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(project._id)}
      data-testid={`campaign-row-${project._id}`}
      data-project-id={project._id}
      aria-label={`Open project ${project.name}`}
      className={`${interactiveSurfaceClasses({
        interactive: true,
      })} flex w-full items-center gap-4 p-4 text-left`}
    >
      <div
        className={interactiveSurfaceIconClasses(
          "flex size-10 shrink-0 items-center justify-center rounded-lg bg-puntt-accent-2 text-puntt-accent-9",
        )}
      >
        <FolderSimplePlus size={20} weight="duotone" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <Text
          size="3"
          weight="medium"
          className="block truncate text-puntt-neutral-gray-12"
        >
          {project.name}
        </Text>
        {project.description ? (
          <Text size="1" className="block truncate text-puntt-neutral-gray-11">
            {project.description}
          </Text>
        ) : null}
      </div>
      <Text size="1" className="shrink-0 text-puntt-neutral-gray-9">
        {formatProjectDate(project.updatedAt)}
      </Text>
    </button>
  );
}

export default function AskPunttCampaigns() {
  const navigate = useNavigate();
  const { startNewProjectChat, isPending: isStartingProject } =
    useStartNewProjectChat("ask_puntt_campaigns");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [view, setView] = useState<ProjectsView>(getStoredView);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const { data, isLoading, isError, isFetching } = useAskPunttProjects({
    scope: "all",
    search: debouncedSearch,
    participantIds,
  });
  const { data: uniqueMembers = [] } = useAskPunttProjectMemberOptions();

  const projects = data?.projects ?? [];
  const isSearchPending = search.trim() !== debouncedSearch.trim();

  const selectedParticipants = uniqueMembers.filter((m) =>
    participantIds.includes(m._id),
  );

  const participantFilterLabel = (() => {
    if (selectedParticipants.length === 0) return "Participants";
    if (selectedParticipants.length === 1) {
      return projectMemberLabel(selectedParticipants[0]);
    }
    return `${selectedParticipants.length} participants`;
  })();

  const toggleParticipant = (memberId: string, checked: boolean) => {
    setParticipantIds((prev) => {
      if (checked) {
        return prev.includes(memberId) ? prev : [...prev, memberId];
      }
      return prev.filter((id) => id !== memberId);
    });
  };

  const handleViewChange = (next: ProjectsView) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  };

  const openProject = (projectId: string) => {
    navigate({
      to: campaignRoute.to,
      params: { projectId },
      search: campaignSearch(),
    });
  };

  const handleNewProject = () => {
    void startNewProjectChat("campaigns_launcher");
  };

  const hasFilters =
    debouncedSearch.trim().length > 0 || participantIds.length > 0;
  const listLoading = isLoading || isFetching || isSearchPending;
  const showNoResults =
    !listLoading && !isError && hasFilters && projects.length === 0;
  const showEmpty =
    !listLoading && !isError && !hasFilters && projects.length === 0;

  return (
    <div
      className="flex h-full flex-col overflow-y-auto"
      data-testid="ask-puntt-campaigns"
    >
      <PageHeader className="justify-between gap-4">
        <Text size="5" weight="bold" className="text-puntt-accent-9">
          Projects
        </Text>
        <Flex align="center" gap="2" wrap="wrap">
          <TextField.Root
            data-testid="campaigns-search"
            placeholder="Search projects"
            size={TOOLBAR_CONTROL_SIZE}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search projects"
            className="min-w-[200px]"
          >
            <TextField.Slot>
              <MagnifyingGlass size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
            </TextField.Slot>
          </TextField.Root>

          <Popover.Root
            open={participantsOpen}
            onOpenChange={setParticipantsOpen}
          >
            <Popover.Trigger>
              <Button
                type="button"
                variant="surface"
                color="gray"
                size={TOOLBAR_CONTROL_SIZE}
                data-testid="campaigns-participants-filter"
                aria-label={
                  participantIds.length > 0
                    ? `Filter by participants, ${participantIds.length} selected`
                    : "Filter by participants"
                }
                className="cursor-pointer"
              >
                <User size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
                <Text
                  size="2"
                  weight="regular"
                  className="text-puntt-neutral-gray-11"
                >
                  {participantFilterLabel}
                </Text>
              </Button>
            </Popover.Trigger>
            <Popover.Content
              align="end"
              className="min-w-[220px] p-2"
              data-testid="campaigns-participants-popover"
            >
              {uniqueMembers.length === 0 ? (
                <Text size="2" className="px-2 py-1 text-puntt-neutral-gray-9">
                  No participants yet
                </Text>
              ) : (
                <div className="flex flex-col gap-1">
                  {uniqueMembers.map((member) => {
                    const checked = participantIds.includes(member._id);
                    const optionId = `campaigns-participant-${member._id}`;
                    return (
                      <label
                        key={member._id}
                        htmlFor={optionId}
                        data-testid={`campaigns-participant-option-${member._id}`}
                        className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm text-puntt-neutral-gray-12 transition-colors hover:bg-puntt-neutral-gray-3"
                      >
                        <Checkbox
                          id={optionId}
                          checked={checked}
                          onCheckedChange={(value) =>
                            toggleParticipant(member._id, value === true)
                          }
                          aria-label={projectMemberLabel(member)}
                        />
                        {projectMemberLabel(member)}
                      </label>
                    );
                  })}
                </div>
              )}
              {participantIds.length > 0 ? (
                <button
                  type="button"
                  data-testid="campaigns-participant-clear"
                  aria-label="Clear participant filters"
                  className="mt-2 w-full cursor-pointer rounded-md border-t border-puntt-neutral-gray-4 px-3 py-2 text-left text-sm text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3"
                  onClick={() => setParticipantIds([])}
                >
                  Clear filters
                </button>
              ) : null}
            </Popover.Content>
          </Popover.Root>

          <ToolbarViewToggle
            value={view}
            onChange={handleViewChange}
            groupLabel="Project view"
            data-testid="campaigns-view-toggle"
            testIds={{
              grid: "campaigns-view-grid",
              list: "campaigns-view-list",
            }}
          />

          <Button
            data-testid="campaigns-new-button"
            size={TOOLBAR_CONTROL_SIZE}
            disabled={isStartingProject}
            onClick={handleNewProject}
            className="cursor-pointer"
          >
            {isStartingProject ? (
              <CircleNotch
                size={14}
                weight="bold"
                className="animate-spin"
                aria-hidden="true"
              />
            ) : (
              <Plus size={14} weight="bold" aria-hidden="true" />
            )}
            New project
          </Button>
        </Flex>
      </PageHeader>

      <div className="mx-auto w-full max-w-[960px] px-6 py-8">
        {listLoading ? (
          <div
            className={
              view === "grid" ? "grid grid-cols-2 gap-4" : "flex flex-col gap-2"
            }
            data-testid="campaigns-skeleton"
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className={
                  view === "grid"
                    ? "h-40 animate-pulse rounded-2xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                    : "h-20 animate-pulse rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                }
              />
            ))}
          </div>
        ) : isError ? (
          <Callout.Root color="red" size="1" data-testid="campaigns-error">
            <Callout.Text>
              Failed to load projects. Please refresh.
            </Callout.Text>
          </Callout.Root>
        ) : showEmpty ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="4"
            className="mt-16"
            data-testid="campaigns-empty"
          >
            <FolderSimplePlus
              size={40}
              weight="duotone"
              className="text-puntt-neutral-gray-8"
              aria-hidden="true"
            />
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              No projects yet
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              Create a project to give Puntt a shared chat, personal chats, and
              versioned documents & context for a body of work.
            </Text>
            <Button
              data-testid="campaigns-empty-new-button"
              disabled={isStartingProject}
              onClick={handleNewProject}
              className="cursor-pointer"
            >
              {isStartingProject ? (
                <CircleNotch
                  size={14}
                  weight="bold"
                  className="animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <Plus size={14} weight="bold" aria-hidden="true" />
              )}
              New project
            </Button>
          </Flex>
        ) : showNoResults ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="3"
            className="mt-16"
            data-testid="campaigns-no-results"
          >
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              No projects match
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              Try adjusting your search or participant filter.
            </Text>
            <Button
              variant="soft"
              color="gray"
              data-testid="campaigns-clear-filters"
              aria-label="Clear all filters"
              className="cursor-pointer"
              onClick={() => {
                setSearch("");
                setParticipantIds([]);
              }}
            >
              Clear filters
            </Button>
          </Flex>
        ) : view === "grid" ? (
          <div className="grid grid-cols-2 gap-4" data-testid="campaigns-grid">
            {projects.map((p) => (
              <ProjectCard key={p._id} project={p} onOpen={openProject} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="campaigns-list">
            {projects.map((p) => (
              <ProjectListRow key={p._id} project={p} onOpen={openProject} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
